'use client';

import React, { useEffect, useRef } from 'react';
import Head from 'next/head';
import { useParams, usePathname } from 'next/navigation';

import { checkBrEnv } from './helpers/checkBrEnv';
import { bloomreachPixelSendHomepageView, bloomreachPixelSendOtherView } from './sendBloomreachPageViewTrackingEvent';
import { brScriptSrc } from './config/brscript.config';
import getPageType from './helpers/getPageType';

/**
 * Bloomreach tracking pixel.
 * We need to load the pixel script and then update the data on the window object.
 * When we change path dynamically we need to update the data and then send it back to bloomreach.
 */
const BloomreachPixel = () => {
    const pathName = usePathname();
    const params = useParams();

    // Pretty sure a useParams change is triggered by a hash change in the URL so we need to track the
    // current path name to make sure the below useEffect logic doesn't run on a hash change.
    const currentPathName = useRef<ReturnType<typeof usePathname>>(null);

    /**
     * Listen to the page type change.
     *
     * If the page type is product then we do not want to send the page view tracking, we will do that
     * manually in treatment selector.
     *
     * Ignore the order-confirmed page because we do that on the page itself with the conversion data.
     *
     * If the page type is category (condition/generic page) then we do not want to send the page view
     * tracking, we will do that manually on that page as we have no way of knowing if it's generic or
     * condition from here.
     */
    useEffect(() => {
        // If bloomreach is not enabled or pathName isn't available or the path name hasn't actually changed,
        // then we don't want to do anything.
        if (checkBrEnv() === false || pathName === null || currentPathName.current === pathName) {
            return;
        }

        currentPathName.current = pathName;

        const pageType = getPageType(pathName, params);

        // NOTE: For category (condition or generic pages), since we can't work out if the page is generic or
        // not from here, bloomreach is handled manually on both pages in their respective components.
        // See 'src/(site)/[dynamicRoute]/_pages'.
        switch (pageType) {
            case 'orderConfirmed':
            case 'search':  // We do this on the page itself for bespoke functionality.
            case 'product': // We do this on the page manually in treatment selector.
            case 'category': // We do this on the page itself for bespoke functionality (/[dynamicRoute]).
                return;

            case 'homepage':
                bloomreachPixelSendHomepageView();
                return;

            default:
                bloomreachPixelSendOtherView();
        }
    }, [pathName, params]);

    return (
        <Head>
            <link rel="preconnect" href={brScriptSrc} />
        </Head>
    );
};

export default BloomreachPixel;
